import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { getPrimaryColorRgb } from '../utils/theme';

/**
 * Line Support "Yordam" vidjeti (markaziy support tizimi).
 *
 * Faqat tashkilotning Integratsiyalar ro'yxatida 'support' bo'lsa ishga tushadi (pilot: alohida tashkilotlar
 * uchun yoqiladi). Token backenddan (`GET /api/support/token`) olinadi; muddati tugaganda SDK yangisini o'zi so'raydi.
 * Backend ham shu shartni tekshiradi, shuning uchun bu yerdagi tekshiruv faqat tugmani ko'rsatish uchun.
 */
const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
// Support vidjeti manzili faqat env'dan (REACT_APP_SUPPORT_URL); berilmasa vidjet o'chiq qoladi.
const supportUrl = () => (process.env.REACT_APP_SUPPORT_URL || '').replace(/\/$/, '');

let sdkPromise = null;
function loadSdk() {
  if (window.LineSupport) return Promise.resolve(window.LineSupport);
  sdkPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${supportUrl()}/sdk.js`;
    script.async = true;
    script.onload = () => resolve(window.LineSupport);
    script.onerror = () => { sdkPromise = null; script.remove(); reject(new Error('Support SDK yuklanmadi')); };
    document.head.appendChild(script);
  });
  return sdkPromise;
}

const fetchToken = async () => (await axios.get(`${API}/support/token`)).data.token;

// Sahifa yo'li → Support'dagi "erp" mahsulotining modul kaliti (tiketda modul avtomatik tanlanadi).
const MODULE_BY_PREFIX = [
  ['/sales', 'sales'], ['/sales-invoice', 'sales'], ['/sales-agent', 'sales'], ['/sales-points', 'sales'],
  ['/purchases', 'purchases'], ['/procurement', 'procurement'],
  ['/products', 'warehouse'], ['/inventarizatsiya', 'warehouse'],
  ['/payments', 'payments'], ['/cashboxes', 'payments'],
  ['/job-orders', 'job_orders'], ['/production', 'production'], ['/process', 'production'],
  ['/counterparties', 'counterparties'], ['/akt-sverka', 'counterparties'],
  ['/settings', 'settings'], ['/dashboard', 'dashboard'],
];
// ERP'da hozir ko'rinib turgan asosiy rang (tashkilot brend rangi yoki standart yashil) -> '#rrggbb'.
// Vidjet ham shu rangda chiqadi. Qiymat CSS o'zgaruvchisidan (--p-600) o'qiladi, shuning uchun sozlanmagan
// (standart) tashkilotlarda ham to'g'ri.
export function primaryColorHex() {
  const m = getPrimaryColorRgb().match(/\d+/g);
  if (!m || m.length < 3) return undefined;
  return `#${m.slice(0, 3).map((n) => Number(n).toString(16).padStart(2, '0')).join('')}`;
}

export function moduleForPath(pathname) {
  // Mobil ko'rinish `/m/dashboard`, `/m/sales/...` ko'rinishida: `/m` prefiksi modulni aniqlashga xalaqit bermasin.
  const path = pathname.replace(/^\/m(?=\/)/, '');
  return MODULE_BY_PREFIX.find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`))?.[1];
}

export default function SupportWidget() {
  const { pathname } = useLocation();
  const userId = useSelector((s) => s.auth.user?.id);
  const orgId = useSelector((s) => s.auth.user?.organization?.id);
  const enabled = useSelector((s) => {
    const list = s.auth.user?.organization?.settings?.integrations;
    return Array.isArray(list) && list.includes('support');
  });
  const language = useSelector((s) => s.lang?.current);
  // Faqat qayta yaratish uchun: tashkilot rangi o'zgarsa vidjet yangi rangda qayta chiqadi.
  const brandColor = useSelector((s) => s.auth.user?.organization?.brandColor);

  // Tizimga kirgan foydalanuvchi/tashkilot/til o'zgarsa vidjet qayta yaratiladi; chiqishda yo'q qilinadi.
  useEffect(() => {
    if (!userId || !enabled || !supportUrl()) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [sdk, token] = await Promise.all([loadSdk(), fetchToken()]);
        if (cancelled) return;
        sdk.init({
          product: 'erp',
          version: process.env.REACT_APP_VERSION || 'web',
          token,
          getToken: fetchToken,
          locale: language === 'ru' ? 'ru' : 'uz',
          widgetUrl: supportUrl(),
          // Token kutilgach o'qiladi: shu paytga kelib ERP temasi (applyTheme) allaqachon qo'llangan bo'ladi.
          themeColor: primaryColorHex(),
        });
        sdk.setContext({ module: moduleForPath(window.location.pathname) });
      } catch {
        // Yordam vidjeti ERP ishiga hech qachon xalaqit bermasligi kerak: xato bo'lsa jimgina o'chiq qoladi.
      }
    })();
    return () => {
      cancelled = true;
      window.LineSupport?.destroy();
    };
  }, [userId, orgId, enabled, language, brandColor]);

  useEffect(() => {
    if (enabled) window.LineSupport?.setContext({ module: moduleForPath(pathname) });
  }, [pathname, enabled]);

  return null;
}
